import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  listInterventions, listContracts, listClients, getSubscribeUrl,
  listManualEvents, createManualEvent, deleteManualEvent, reschedulePlan,
  confirmIntervention, unconfirmIntervention, updateIntervention, acceptIntervention,
  gcalSyncNow, monthlyAgendaUrl, clearAllCalendar, deleteIntervention, duplicateIntervention,
  moveIntervention, listHolidays,
} from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Download, ChevronLeft, ChevronRight, ExternalLink, Plus, Trash2, RefreshCw,
  CheckCircle2, Mail, MapPin, Phone, User, Clock, Star, FileSpreadsheet, AlertTriangle, Copy, Ban, StickyNote,
} from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DAYS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const SLOT_LABEL = { full: "Giornata intera (09:00 - 18:00)", morning: "Mattina (09:00 - 13:30)", afternoon: "Pomeriggio (14:30 - 18:00)" };
const SLOT_SHORT = { full: "GG", morning: "AM", afternoon: "PM" };
const ivTimeLabel = (it) => (it?.start_time && it?.end_time) ? `${it.start_time}-${it.end_time}` : SLOT_LABEL[it?.slot || "full"];
const ivTimeShort = (it) => (it?.start_time && it?.end_time) ? it.start_time : SLOT_SHORT[it?.slot || "full"];

const QuickDelete = ({ testid, onClick }) => (
  <button type="button" data-testid={testid} title="Cancella appuntamento"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    className="shrink-0 p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
    <Trash2 className="w-3 h-3" />
  </button>
);


export default function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ivs, setIvs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [manual, setManual] = useState([]);
  const [subUrl, setSubUrl] = useState("");
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [addOpen, setAddOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [holidays, setHolidays] = useState({});  // { "YYYY-MM-DD": "Festa nome" }
  const [form, setForm] = useState({ date: "", title: "", client_id: "none", notes: "", slot: "full", start_time: "", end_time: "" });

  const load = () => Promise.all([listInterventions(), listContracts(), listClients(), listManualEvents(), getSubscribeUrl(), listHolidays()])
    .then(([i, c, cl, m, s, h]) => { setIvs(i); setContracts(c); setClients(cl); setManual(m); setSubUrl(s.url); setHolidays(h.holidays || {}); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  // Auto-open intervention detail if ?open=<ivId> is present in URL
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || ivs.length === 0) return;
    const iv = ivs.find((x) => x.id === openId);
    if (iv) {
      // Move calendar view to the month of the target intervention
      try {
        const d = new Date(iv.date);
        d.setDate(1);
        setMonthDate(d);
      } catch { /* ignore */ }
      setDetailItem({ ...iv, _kind: "iv" });
      setDetailOpen(true);
      // Clear the query param so it doesn't re-open on refresh
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [ivs, searchParams, setSearchParams]);

  const byDate = useMemo(() => {
    const m = {};
    for (const iv of ivs) { (m[iv.date] = m[iv.date] || []).push({ ...iv, _kind: "iv" }); }
    for (const ev of manual) { (m[ev.date] = m[ev.date] || []).push({ ...ev, _kind: "manual" }); }
    return m;
  }, [ivs, manual]);

  const cli = (id) => clients.find((c) => c.id === id);
  const ctr = (id) => contracts.find((c) => c.id === id);

  const monthBlocks = useMemo(() => {
    const buildMonth = (y, m) => {
      const first = new Date(y, m, 1);
      const shift = (first.getDay() + 6) % 7;
      const days = new Date(y, m + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < shift; i++) cells.push(null);
      for (let d = 1; d <= days; d++) {
        const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        cells.push({ d, iso });
      }
      while (cells.length % 7 !== 0) cells.push(null);
      return { y, m, cells, label: `${MONTHS[m]} ${y}` };
    };
    const blocks = [];
    const base = new Date(monthDate);
    for (let i = 0; i < 2; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
      blocks.push(buildMonth(d.getFullYear(), d.getMonth()));
    }
    return blocks;
  }, [monthDate]);

  const changeMonth = (delta) => { const d = new Date(monthDate); d.setMonth(d.getMonth() + delta); setMonthDate(d); };
  const downloadIcs = () => subUrl && window.open(subUrl, "_blank");
  const openInGoogle = () => window.open("https://calendar.google.com/calendar/u/0/r/settings/addbyurl", "_blank");

  const openAdd = (iso) => { setForm({ date: iso || "", title: "", client_id: "none", notes: "", slot: "full", start_time: "", end_time: "", blocking: false }); setAddOpen(true); };
  const openBlocking = (iso) => { setForm({ date: iso || "", title: "Giornata bloccata", client_id: "none", notes: "", slot: "full", start_time: "", end_time: "", blocking: true }); setAddOpen(true); };
  const openDetail = (item) => { setDetailItem(item); setDetailOpen(true); };
  const quickDelete = async (it) => {
    const label = it._kind === "manual" ? (it.title || "evento") : (cli(it.client_id)?.name || "intervento");
    if (!window.confirm(`Cancellare "${label}" del ${it.date.split("-").reverse().join("/")}? L'operazione non è reversibile.`)) return;
    try {
      if (it._kind === "manual") await deleteManualEvent(it.id);
      else await deleteIntervention(it.id);
      toast.success("Appuntamento cancellato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const submit = async () => {
    if (!form.date || !form.title) { toast.error("Data e titolo obbligatori"); return; }
    try {
      await createManualEvent({
        date: form.date, title: form.title, notes: form.notes,
        client_id: form.client_id === "none" ? null : form.client_id,
        slot: form.slot, all_day: form.slot === "full" && !form.start_time,
        start_time: form.start_time || null, end_time: form.end_time || null,
        blocking: !!form.blocking,
      });
      toast.success(form.blocking ? "Giornata bloccata" : "Appuntamento aggiunto");
      setAddOpen(false); load();
    } catch (_e) { toast.error("Errore"); }
  };

  const removeManual = async (id) => {
    if (!confirm("Rimuovere questo appuntamento?")) return;
    await deleteManualEvent(id); toast.success("Rimosso"); setDetailOpen(false); load();
  };

  const doConfirm = async () => {
    try {
      const r = await confirmIntervention(detailItem.id);
      if (r.warning) toast.warning(r.warning);
      else toast.success("Confermato ed email inviata al cliente");
      const fresh = await listInterventions();
      setIvs(fresh);
      const updated = fresh.find((x) => x.id === detailItem.id);
      if (updated) setDetailItem({ ...updated, _kind: "iv" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const doUnconfirm = async () => {
    try {
      await unconfirmIntervention(detailItem.id);
      toast.success("Riportato a pianificato");
      const fresh = await listInterventions();
      setIvs(fresh);
      const updated = fresh.find((x) => x.id === detailItem.id);
      if (updated) setDetailItem({ ...updated, _kind: "iv" });
    } catch (_e) { toast.error("Errore"); }
  };

  const doAccept = async () => {
    try {
      await acceptIntervention(detailItem.id);
      toast.success("Segnato come ACCETTATO dal cliente");
      const fresh = await listInterventions();
      setIvs(fresh);
      const updated = fresh.find((x) => x.id === detailItem.id);
      if (updated) setDetailItem({ ...updated, _kind: "iv" });
    } catch (_e) { toast.error("Errore"); }
  };

  const handleDrop = async (e, targetIso) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const iv = ivs.find((x) => x.id === id);
    if (!iv || iv.date === targetIso) return;
    try {
      await updateIntervention(id, { date: targetIso });
      toast.success(`Spostato al ${targetIso}`);
      const fresh = await listInterventions();
      setIvs(fresh);
      const updated = fresh.find((x) => x.id === id);
      if (updated) { setDetailItem({ ...updated, _kind: "iv" }); setDetailOpen(true); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore spostamento");
    }
  };

  const changeSlotInDetail = async (newSlot) => {
    try {
      await updateIntervention(detailItem.id, { slot: newSlot });
      toast.success("Fascia oraria aggiornata");
      setDetailItem({ ...detailItem, slot: newSlot });
      load();
    } catch (_e) { toast.error("Errore"); }
  };

  const reschedule = async () => {
    if (!confirm("Riprogrammare tutti gli interventi da capo? Gli appuntamenti extra saranno rispettati.")) return;
    setRescheduling(true);
    try {
      const r = await reschedulePlan({ workdays_only: true, max_days_per_client_block: 1 });
      toast.success(`Riprogrammati ${r.planned} interventi`);
      load();
    } catch (_e) { toast.error("Errore riprogrammazione"); }
    finally { setRescheduling(false); }
  };

  const [syncingGcal, setSyncingGcal] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearInput, setClearInput] = useState("");
  const [clearing, setClearing] = useState(false);

  const clearAll = async () => {
    if (clearInput !== "CANCELLA") { toast.error("Digita CANCELLA per confermare"); return; }
    setClearing(true);
    try {
      const r = await clearAllCalendar({ confirm: "CANCELLA", include_manual: true });
      toast.success(`Cancellati ${r.interventions_deleted} interventi + ${r.manual_events_deleted} appuntamenti extra`);
      setClearOpen(false);
      setClearInput("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione");
    } finally {
      setClearing(false);
    }
  };
  const syncGcal = async () => {
    setSyncingGcal(true);
    try {
      const r = await gcalSyncNow();
      if (r.skipped) toast.warning("URL iCal Google non configurato o disattivato. Vai in Impostazioni.");
      else toast.success(`${r.synced} eventi Google Calendar sincronizzati`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync Google fallito"); }
    finally { setSyncingGcal(false); }
  };

  return (
    <div data-testid="calendar-view-section" className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-medium tracking-wider uppercase text-slate-500">Vista mensile</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold font-display text-slate-900">Calendario</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="add-manual-event-button" variant="outline" onClick={() => openAdd("")}>
            <Plus className="w-4 h-4 mr-2" /> Appuntamento extra
          </Button>
          <Button data-testid="add-blocking-day-button" variant="outline" onClick={() => openBlocking("")}
                  className="border-red-300 text-red-800 hover:bg-red-50">
            <Ban className="w-4 h-4 mr-2" /> Blocca giornata
          </Button>
          <Button data-testid="sync-gcal-now-button" variant="outline" onClick={syncGcal} disabled={syncingGcal}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingGcal ? "animate-spin" : ""}`} /> Sync Google Cal
          </Button>
          <Button
            data-testid="monthly-agenda-button"
            variant="outline"
            onClick={() => {
              const y = monthDate.getFullYear();
              const m = String(monthDate.getMonth() + 1).padStart(2, "0");
              window.open(monthlyAgendaUrl(`${y}-${m}`), "_blank");
            }}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Agenda Excel
          </Button>
          <Button data-testid="reschedule-all-button" variant="outline" onClick={reschedule} disabled={rescheduling}>
            <RefreshCw className={`w-4 h-4 mr-2 ${rescheduling ? "animate-spin" : ""}`} /> Riprogramma tutto
          </Button>
          <Button data-testid="download-ics-button" variant="outline" onClick={downloadIcs}>
            <Download className="w-4 h-4 mr-2" /> .ics
          </Button>
          <Button data-testid="open-gcal-button" onClick={openInGoogle} className="bg-slate-900 hover:bg-slate-800 text-white">
            <ExternalLink className="w-4 h-4 mr-2" /> Google Cal.
          </Button>
          <Button data-testid="clear-all-calendar-button" variant="outline" onClick={() => { setClearInput(""); setClearOpen(true); }} className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">
            <Trash2 className="w-4 h-4 mr-2" /> Cancella tutto
          </Button>
        </div>
      </div>

      {/* Danger: clear all interventions + manual events */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent data-testid="clear-all-dialog" className="bg-white sm:max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Cancella tutto il calendario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-900">
              Stai per cancellare <b>tutti gli interventi pianificati</b> (compresi confermati e accettati) e <b>tutti gli appuntamenti extra manuali</b>. Gli eventi importati da Google Calendar restano.
              <br /><br />
              I contratti pianificati tornano allo stato "attivo" e potrai ripianificarli da zero.
              <br /><br />
              <b>L'operazione non è reversibile.</b>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Per confermare, digita <span className="font-mono text-red-700">CANCELLA</span> qui sotto:</Label>
              <Input
                data-testid="clear-all-confirm-input"
                autoFocus
                value={clearInput}
                onChange={(e) => setClearInput(e.target.value)}
                placeholder="CANCELLA"
                className="mt-2 font-mono"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="clear-all-cancel" variant="outline" onClick={() => { setClearOpen(false); setClearInput(""); }}>
              Annulla
            </Button>
            <Button
              data-testid="clear-all-confirm"
              onClick={clearAll}
              disabled={clearing || clearInput !== "CANCELLA"}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            >
              {clearing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Sì, cancella tutto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-4 sm:p-6 border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <Button data-testid="prev-month" variant="ghost" size="sm" onClick={() => changeMonth(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-bold font-display text-lg text-slate-900">
            {monthBlocks[0].label} <span className="text-slate-400 mx-1">→</span> {monthBlocks[monthBlocks.length - 1].label}
          </div>
          <Button data-testid="next-month" variant="ghost" size="sm" onClick={() => changeMonth(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto pr-1 space-y-8" data-testid="calendar-scroll">
          {monthBlocks.map((block, bi) => (
            <div key={`${block.y}-${block.m}`} data-testid={`month-block-${block.y}-${block.m}`}>
              {bi > 0 && (
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 py-2 mb-2 -mx-1 px-2">
                  <div className="text-sm font-bold font-display text-slate-900">{block.label}</div>
                </div>
              )}
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {DAYS.map((d) => (
                  <div key={`${block.y}-${block.m}-${d}`} className="text-[10px] sm:text-xs font-semibold uppercase text-slate-500 text-center py-2 tracking-wider">{d}</div>
                ))}
                {block.cells.map((cell, idx) => {
                  if (!cell) return <div key={idx} className="min-h-[72px] sm:min-h-[110px]" />;
                  const items = byDate[cell.iso] || [];
                  const isToday = cell.iso === new Date().toISOString().slice(0, 10);
                  const dayOfWeek = new Date(cell.iso + "T00:00:00").getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const holidayName = holidays[cell.iso];
                  const isHoliday = !!holidayName;
                  const hasBlocking = items.some((it) => it._kind === "manual" && it.blocking);
                  const isBlocked = isHoliday || hasBlocking;
                  return (
                    <div
                      key={idx}
                      data-testid={`day-cell-${cell.iso}`}
                      onDragOver={(e) => { if (!isBlocked) e.preventDefault(); }}
                      onDrop={(e) => { if (!isBlocked) handleDrop(e, cell.iso); }}
                      className={`min-h-[72px] sm:min-h-[110px] p-1.5 sm:p-2 rounded-md border text-left overflow-hidden transition-colors ${
                        isBlocked ? "border-red-300 bg-red-50 cursor-not-allowed"
                        : isWeekend ? "border-slate-200 bg-slate-100/60"
                        : isToday ? "border-blue-600 bg-blue-50/50"
                        : "border-slate-100 bg-slate-50/40"
                      }`}
                      title={holidayName || ""}
                    >
                      <div className="flex items-center justify-between">
                        <div className={`text-xs font-semibold ${isHoliday ? "text-red-700" : isToday ? "text-blue-700" : "text-slate-700"}`}>
                          {cell.d}
                          {isHoliday && <span className="ml-1 text-[9px] font-bold uppercase tracking-wide">Festa</span>}
                        </div>
                  {!isBlocked && (
                    <button
                      type="button"
                      data-testid={`add-day-${cell.iso}`}
                      onClick={() => openAdd(cell.iso)}
                      className="opacity-40 hover:opacity-100 transition-opacity"
                      title="Aggiungi appuntamento extra"
                    >
                      <Plus className="w-3 h-3 text-slate-600" />
                    </button>
                  )}
                </div>
                {isHoliday && (
                  <div className="mt-1 text-[10px] text-red-700 font-semibold leading-tight truncate" title={holidayName}>
                    {holidayName}
                  </div>
                )}
                <div className="mt-1 space-y-1">
                  {items.slice(0, 3).map((it) => {
                    if (it._kind === "manual") {
                      const isBlockingEv = !!it.blocking;
                      return (
                        <div key={it.id} className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => openDetail(it)}
                          data-testid={`event-${it.id}`}
                          className={`flex-1 min-w-0 text-[10px] leading-tight px-1.5 py-1 rounded truncate flex items-center gap-1 ${
                            isBlockingEv
                              ? "bg-red-200 text-red-900 hover:bg-red-300 border border-red-400 font-bold"
                              : "bg-amber-100 text-amber-900 hover:bg-amber-200"
                          }`}
                        >
                          {isBlockingEv ? <Ban className="w-3 h-3" /> : <span className="font-mono mr-1">{ivTimeShort(it)}</span>}
                          <span className="font-semibold truncate flex-1 text-left">{isBlockingEv ? "" : "★ "}{it.title}</span>
                        </button>
                        <QuickDelete testid={`quick-delete-event-${it.id}`} onClick={() => quickDelete(it)} />
                        </div>
                      );
                    }
                    const clic = cli(it.client_id);
                    const c = ctr(it.contract_id);
                    // Color coding: planned=giallo, confirmed(email inviata)=bianco, accepted=verde
                    let cls;
                    if (it.status === "accepted") {
                      cls = "bg-emerald-500 text-white hover:bg-emerald-600 border border-emerald-600";
                    } else if (it.status === "confirmed") {
                      cls = "bg-white text-slate-900 border border-slate-300 hover:bg-slate-50";
                    } else {
                      cls = "bg-yellow-300 text-yellow-950 hover:bg-yellow-400 border border-yellow-500";
                    }
                    return (
                      <div key={it.id} className="flex items-center gap-0.5">
                      <button
                        type="button"
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData("text/plain", it.id); e.dataTransfer.effectAllowed = "move"; }}
                        onClick={() => openDetail(it)}
                        data-testid={`event-${it.id}`}
                        className={`flex-1 min-w-0 text-[10px] leading-tight px-1.5 py-1 rounded truncate text-left cursor-move ${cls}`}
                        title="Trascina per spostare"
                      >
                        <span className="font-mono mr-1">{ivTimeShort(it)}</span>
                        <span className="font-semibold">{clic?.name?.slice(0, 12) || "—"}</span>
                        <span className="hidden sm:inline"> · g{it.day_index}/{c?.total_days}</span>
                        {it.status === "accepted" && <span className="ml-1">✓✓</span>}
                        {it.status === "confirmed" && <span className="ml-1">✉</span>}
                      </button>
                      <QuickDelete testid={`quick-delete-event-${it.id}`} onClick={() => quickDelete(it)} />
                      </div>
                    );
                  })}
                  {items.length > 3 && (
                    <div className="text-[10px] text-slate-500 px-1.5">+{items.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Add manual event dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white sm:max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.blocking ? "Blocca giornata" : "Nuovo appuntamento extra"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {form.blocking && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                <Ban className="w-3.5 h-3.5 inline mr-1" /> Le giornate bloccate impediscono qualsiasi pianificazione (incluse scadenze).
              </div>
            )}
            <div>
              <Label>Data *</Label>
              <Input data-testid="manual-event-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Titolo *</Label>
              <Input data-testid="manual-event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="es. Riunione con avvocato" />
            </div>
            <div>
              <Label>Fascia oraria</Label>
              <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                <SelectTrigger data-testid="manual-event-slot" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="full">Giornata intera (09:00 - 18:00)</SelectItem>
                  <SelectItem value="morning">Mattina (09:00 - 13:30)</SelectItem>
                  <SelectItem value="afternoon">Pomeriggio (14:30 - 18:00)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Orario personalizzato (opzionale — se compilato ha priorità sulla fascia)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="manual-event-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} placeholder="Inizio" />
                <Input data-testid="manual-event-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} placeholder="Fine" />
              </div>
            </div>
            <div>
              <Label>Cliente (opzionale)</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="manual-event-client" className="bg-white"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— Nessuno —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea data-testid="manual-event-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
              Il planning e la riprogrammazione automatica rispetteranno questa fascia oraria come occupata.
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="manual-event-save" onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white">
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail panel */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid="event-detail-dialog" className="bg-white sm:max-w-lg max-h-[92vh] overflow-y-auto">
          {detailItem && detailItem._kind === "iv" ? (() => {
            const clic = cli(detailItem.client_id);
            const c = ctr(detailItem.contract_id);
            const status = detailItem.status || "planned";
            const isAccepted = status === "accepted";
            const isConfirmed = status === "confirmed";
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {isAccepted ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-500 text-white rounded-md font-medium uppercase tracking-wide">
                        <CheckCircle2 className="w-3 h-3" /> Accettato dal cliente
                      </span>
                    ) : isConfirmed ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-white text-slate-900 border border-slate-300 rounded-md font-medium uppercase tracking-wide">
                        <Mail className="w-3 h-3" /> Email inviata
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-yellow-300 text-yellow-950 border border-yellow-500 rounded-md font-medium uppercase tracking-wide">Pianificato</span>
                    )}
                    <span className="text-slate-900">{c?.title || "Intervento"}</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  {/* 1) CLIENTE */}
                  {clic && (
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cliente</div>
                      <div className="text-sm font-bold text-slate-900">{clic.name}</div>
                      {clic.address && <div className="text-xs text-slate-600 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {clic.address}{clic.city ? `, ${clic.city}` : ""}</div>}
                      {clic.contact_name && <div className="text-xs text-slate-600 flex items-center gap-1.5"><User className="w-3 h-3" /> {clic.contact_name}</div>}
                      {clic.phone && <div className="text-xs text-slate-600 flex items-center gap-1.5"><Phone className="w-3 h-3" /> <a href={`tel:${clic.phone}`} className="underline">{clic.phone}</a></div>}
                      {clic.email && <div className="text-xs text-slate-600 flex items-center gap-1.5"><Mail className="w-3 h-3" /> <a href={`mailto:${clic.email}`} className="underline">{clic.email}</a></div>}
                    </div>
                  )}
                  {/* 2) DATA + giorno */}
                  <div className="grid grid-cols-2 gap-3 pb-3 border-b border-slate-100">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Data</Label>
                      <Input
                        data-testid="detail-date-input"
                        type="date"
                        value={detailItem.date}
                        onChange={async (e) => {
                          const newDate = e.target.value;
                          try {
                            await updateIntervention(detailItem.id, { date: newDate });
                            toast.success(`Spostato al ${newDate}`);
                            setDetailItem({ ...detailItem, date: newDate });
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Errore");
                          }
                        }}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Giornata</div>
                      <div className="flex items-center gap-1 pt-1">
                        <Input
                          data-testid="detail-day-index-input"
                          type="number"
                          min="1"
                          max={c?.total_days ? Math.ceil(c.total_days) : undefined}
                          defaultValue={detailItem.day_index}
                          onBlur={async (e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!v || v === detailItem.day_index) return;
                            try {
                              await updateIntervention(detailItem.id, { day_index: v });
                              setDetailItem({ ...detailItem, day_index: v });
                              toast.success("Giornata aggiornata");
                              load();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Errore");
                              e.target.value = detailItem.day_index;
                            }
                          }}
                          className="h-9 w-16 text-center font-mono text-sm font-semibold"
                        />
                        <span className="text-sm text-slate-500 font-mono">/ {c?.total_days ? Math.ceil(c.total_days) : "?"}</span>
                      </div>
                    </div>
                  </div>
                  {/* 3) ORARIO */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1">
                      <Clock className="w-3 h-3" /> Fascia oraria
                    </Label>
                    <Select value={detailItem.slot || "full"} onValueChange={changeSlotInDetail}>
                      <SelectTrigger data-testid="detail-slot-select" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="full">{SLOT_LABEL.full}</SelectItem>
                        <SelectItem value="morning">{SLOT_LABEL.morning}</SelectItem>
                        <SelectItem value="afternoon">{SLOT_LABEL.afternoon}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Orario personalizzato (opzionale)</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Input
                        data-testid="detail-start-time"
                        type="time"
                        value={detailItem.start_time || ""}
                        onBlur={async (e) => {
                          const v = e.target.value || "";
                          if (v === (detailItem.start_time || "")) return;
                          try {
                            await updateIntervention(detailItem.id, { start_time: v });
                            toast.success("Orario inizio aggiornato");
                            setDetailItem({ ...detailItem, start_time: v || null });
                            load();
                          } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
                        }}
                        onChange={(e) => setDetailItem({ ...detailItem, start_time: e.target.value })}
                      />
                      <Input
                        data-testid="detail-end-time"
                        type="time"
                        value={detailItem.end_time || ""}
                        onBlur={async (e) => {
                          const v = e.target.value || "";
                          if (v === (detailItem.end_time || "")) return;
                          try {
                            await updateIntervention(detailItem.id, { end_time: v });
                            toast.success("Orario fine aggiornato");
                            setDetailItem({ ...detailItem, end_time: v || null });
                            load();
                          } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
                        }}
                        onChange={(e) => setDetailItem({ ...detailItem, end_time: e.target.value })}
                      />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Se compilato, ha priorità sulla fascia. Lascia vuoto per usare la fascia oraria.</div>
                  </div>
                  {clic && (
                    <div className="hidden">{/* moved to top */}</div>
                  )}
                  {/* 3.5) NOTE INTERVENTO (editabile) */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1">
                      <StickyNote className="w-3 h-3" /> Note intervento
                    </Label>
                    <Textarea
                      data-testid="detail-notes-input"
                      value={detailItem.notes || ""}
                      onChange={(e) => setDetailItem({ ...detailItem, notes: e.target.value })}
                      onBlur={async (e) => {
                        const v = e.target.value || "";
                        if (v === (detailItem.notes || "")) return;
                        try {
                          await updateIntervention(detailItem.id, { notes: v });
                          toast.success("Note aggiornate");
                          load();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Errore");
                        }
                      }}
                      placeholder="Dettagli, riferimenti, materiali da portare..."
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                  {/* 4) NOTE - descrizione breve servizio */}
                  {(c?.short_description || c?.title) && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Descrizione servizio</div>
                      <div className="text-sm text-slate-800 bg-blue-50 border border-blue-100 rounded p-2 whitespace-pre-wrap">{c.short_description || c.title}</div>
                    </div>
                  )}
                  {c?.notes && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Note contratto</div>
                      <div className="text-xs text-slate-700 bg-slate-50 p-2 rounded border border-slate-100">{c.notes}</div>
                    </div>
                  )}
                  {isConfirmed && detailItem.confirmed_at && (
                    <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
                      <div>Email di conferma inviata il {new Date(detailItem.confirmed_at).toLocaleString("it-IT")}</div>
                      <div className="mt-1 text-slate-500">In attesa risposta cliente a <span className="font-mono">enrluc@gmail.com</span></div>
                    </div>
                  )}
                  {isAccepted && detailItem.accepted_at && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                      Accettazione registrata il {new Date(detailItem.accepted_at).toLocaleString("it-IT")}
                    </div>
                  )}
                </div>
                <DialogFooter className="flex gap-2 flex-wrap">
                  {!isConfirmed && !isAccepted && (
                    <Button
                      data-testid="detail-confirm"
                      onClick={doConfirm}
                      disabled={!clic?.email}
                      title={clic?.email ? "Invia email di conferma al cliente" : "Cliente senza email in anagrafica — impossibile inviare conferma"}
                      className="bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Mail className="w-4 h-4 mr-2" /> {clic?.email ? "Invia email conferma" : "Manca email cliente"}
                    </Button>
                  )}
                  {isConfirmed && !isAccepted && (
                    <>
                      <Button data-testid="detail-accept" onClick={doAccept} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Cliente ha accettato
                      </Button>
                      <Button data-testid="detail-unconfirm" variant="outline" onClick={doUnconfirm}>Annulla email</Button>
                    </>
                  )}
                  {isAccepted && (
                    <Button data-testid="detail-unconfirm" variant="outline" onClick={doUnconfirm}>Riporta a pianificato</Button>
                  )}
                  <Button
                    data-testid="detail-duplicate-intervention"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const r = await duplicateIntervention(detailItem.id);
                        toast.success(`Duplicato al ${r.date}`);
                        setDetailItem(null);
                        load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Errore duplicazione");
                      }
                    }}
                    className="text-blue-700 border-blue-200 hover:bg-blue-50 hover:text-blue-800"
                  >
                    <Copy className="w-4 h-4 mr-2" /> Duplica
                  </Button>
                  <Button
                    data-testid="detail-delete-intervention"
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm("Cancellare questo intervento? L'operazione non è reversibile.")) return;
                      try {
                        await deleteIntervention(detailItem.id);
                        toast.success("Intervento cancellato");
                        setDetailItem(null);
                        load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Errore");
                      }
                    }}
                    className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800 ml-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Cancella intervento
                  </Button>
                </DialogFooter>
              </>
            );
          })() : detailItem && detailItem._kind === "manual" ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-600" />
                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md font-medium uppercase tracking-wide">Extra</span>
                  <span className="text-slate-900">{detailItem.title}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 pb-3 border-b border-slate-100">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Data</div>
                    <div className="text-sm font-mono font-semibold text-slate-900">{detailItem.date}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Fascia oraria</div>
                    <div className="text-sm font-semibold text-slate-900">{ivTimeLabel(detailItem)}</div>
                  </div>
                </div>
                {detailItem.client_id && cli(detailItem.client_id) && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cliente</div>
                    <div className="text-sm font-bold text-slate-900">{cli(detailItem.client_id).name}</div>
                  </div>
                )}
                {detailItem.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Note</div>
                    <div className="text-xs text-slate-700 bg-slate-50 p-2 rounded border border-slate-100 whitespace-pre-wrap">{detailItem.notes}</div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="detail-remove-manual" variant="outline" onClick={() => removeManual(detailItem.id)} className="text-red-700 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Rimuovi
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
